import { Box, Divider, Grid, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material';
import { Button, Card, Modal, Page, Pagination, ToggleGroup } from '@perry-weather/component-library';
import { Redirect } from 'wouter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'oidc-react';
import { useDispatch } from 'react-redux';
import { openToast } from '../features/toast/toastSlice';
import {
  useNotiUsersQuery,
  useSitesQuery,
  useUsersQuery,
  Communication,
  ModelStateErrorResponse,
  CommunicationErrorModel,
  useMassMessageHistoryQuery,
  MassMessageHistory,
  useMassMessageTemplatesQuery,
  useDeleteTemplateMutation,
} from '../Services/API';
import { Chat, Search } from '@mui/icons-material';
import { withRenderTracking } from '../Components/Tracked/withMixpanelTracking';
import { TrackableEvents } from '../Components/Tracked/events';
import mixpanel from 'mixpanel-browser';
import { Permissions, UserRoles } from '../Constants/Constants';
import { usePermissionGate } from '../Components/Shared/PermissionGate';
import MessageCard from '../Components/MassNotify/MessageCard';
import MessageForm from '../Components/MassNotify/MessageForm';
import { DEFAULT_CHANNEL, DEFAULT_OVERRIDE_PREFERENCE } from '../Constants/MassMessageConstants';

const SUBMENUS = [
  { value: 'TEMPLATES', label: 'Templates' },
  { value: 'HISTORY', label: 'History' },
];

const TrackedPage = withRenderTracking(Page)(TrackableEvents.PageViewed, { page_name: 'Mass Notify' });

export function MassNotify() {
  const theme = useTheme();

  const dispatch = useDispatch();

  const AMT_PER_PAGE = 5;

  const [communication, setCommunication] = useState<Communication>({
    recipients: [],
    headline: '',
    senderText: '',
    message: '',
    channel: DEFAULT_CHANNEL,
    overridePreferences: DEFAULT_OVERRIDE_PREFERENCE,
  });
  const [errorState, setErrorState] = useState<CommunicationErrorModel>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('HISTORY');
  const [filteredHistory, setFilteredHistory] = useState<MassMessageHistory[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<MassMessageHistory[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [openDeleteConfirmationModal, setDeleteOpenConfirmationModal] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<MassMessageHistory | undefined>(undefined);

  const { data: messageHistory } = useMassMessageHistoryQuery();

  // Templates
  const { data: messageTemplates } = useMassMessageTemplatesQuery();

  const [
    deleteTemplate,
    {
      isSuccess: isDeleteTemplateSuccess,
      isLoading: isDeleteTemplateLoading,
      isError: isDeleteTemplateError,
      error: deleteTemplateError,
    },
  ] = useDeleteTemplateMutation();

  const { userData } = useAuth();
  const { data: notiUsers } = useNotiUsersQuery();
  const { data: users } = useUsersQuery();
  const { data: sites } = useSitesQuery();
  const currentUser = users?.find(u => u.id === userData?.profile?.sub);

  const storedCommunication = useRef<Communication>();

  const slicedHistory = filteredHistory?.slice(
    AMT_PER_PAGE * (currentPage - 1),
    AMT_PER_PAGE * (currentPage - 1) + AMT_PER_PAGE
  ); //0,2 | 3,5 | 6,8 | 9,10

  const slicedTemplates = filteredTemplates?.slice(
    AMT_PER_PAGE * (currentPage - 1),
    AMT_PER_PAGE * (currentPage - 1) + AMT_PER_PAGE
  );

  const hasMassNotify = usePermissionGate({
    allowedPermissions: [Permissions.MassNotify],
    allowedRoles: [UserRoles.SuperAdmin, UserRoles.Admin, UserRoles.AssistantAdmin],
  });

  const { historyList } = useMemo(() => {
    if (!messageHistory) return { historyList: [] };

    const historyList: MassMessageHistory[] = [];

    messageHistory.forEach(ms => {
      historyList.push(ms);
    });

    return { historyList };
  }, [messageHistory]);

  const { templatesList } = useMemo(() => {
    if (!messageTemplates) return { templatesList: [] };

    const templatesList: MassMessageHistory[] = [];
    messageTemplates.forEach(ms => {
      templatesList.push(ms);
    });

    return { templatesList };
  }, [messageTemplates]);

  useEffect(() => {
    setCommunication({
      ...communication,
      senderText: `${currentUser?.fullName || ''} at ${currentUser?.customerName} using Perry Weather`,
    });
  }, [currentUser]);

  useEffect(() => {
    setFilteredHistory(historyList);
  }, [historyList]);

  useEffect(() => {
    setFilteredTemplates(templatesList);
  }, [templatesList]);

  const handleCopyMessage = (mh: MassMessageHistory) => {
    if (mh.isTemplate) {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        button_location: 'Mass Notify',
        button_feature: 'Mass Notify',
        button_label: 'Edit Template',
        button_type: 'button',
      });
    } else {
      mixpanel.track(TrackableEvents.ButtonClicked, {
        button_location: 'Mass Notify',
        button_feature: 'Mass Notify',
        button_label: 'Edit History',
        button_type: 'button',
      });
    }

    let selectedRecipIds = mh.deliveredRecipients.map(x => x.recipientId);
    const copiedMessage = {
      ...communication,
      // Set id only if template, for override purposes
      id: mh.isTemplate ? mh.id : undefined,
      headline: mh.headline ?? '',
      senderText: mh.senderText ?? '',
      message: mh.message,
      channel: mh.channel,
      isTemplate: mh.isTemplate,
      overridePreferences: mh.overridePreferences != null ? mh.overridePreferences : true,
      recipients: selectedRecipIds,
    };

    storedCommunication.current = copiedMessage;
    setCommunication(copiedMessage);
  };

  const handleDelete = (template: MassMessageHistory) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      button_location: 'Mass Notify',
      button_feature: 'Mass Notify',
      button_label: 'Delete Template',
      button_type: 'button',
    });

    setTemplateToDelete(template);
    setDeleteOpenConfirmationModal(true);
  };

  const handleCancelDelete = () => {
    setTemplateToDelete(undefined);
    setDeleteOpenConfirmationModal(false);
  };

  const handleDeleteTemplate = () => {
    if (!templateToDelete?.id) {
      handleCancelDelete();
      return;
    }
    if (communication.id && communication.id === templateToDelete.id) {
      setCommunication({ ...communication, isTemplate: false });
    }
    setTemplateToDelete(undefined);
    setDeleteOpenConfirmationModal(false);
    deleteTemplate(templateToDelete.id);
  };

  const handleSubmenuChange = (newSubmenu: string) => {
    if (newSubmenu === 'HISTORY') {
      setFilteredHistory(historyList);
    }

    if (newSubmenu === 'TEMPLATES') {
      setFilteredTemplates(templatesList);
    }

    setSearchString('');
    setSelectedSubmenu(newSubmenu);
  };

  const handleSearch = useCallback(
    (searchValue: string, submenu: string) => {
      if (!messageHistory) return;

      setSearchString(searchValue);
      const normalizedValue = searchValue.toLowerCase();

      if (submenu === 'HISTORY') {
        if (!searchValue.length) {
          setFilteredHistory(historyList);
          return;
        }

        mixpanel.track(TrackableEvents.ButtonClicked, {
          button_location: 'Mass Notify',
          button_feature: 'Mass Notify',
          button_label: 'Search History',
          button_type: 'input',
        });

        setFilteredHistory(
          historyList.filter(ms => {
            return (
              ms.headline?.toLowerCase().includes(normalizedValue) ||
              ms.message?.toLowerCase().includes(normalizedValue) ||
              ms.senderText?.toLowerCase().includes(normalizedValue)
            );
          })
        );
      }

      if (submenu === 'TEMPLATES') {
        if (!searchValue.length) {
          setFilteredTemplates(templatesList);
          return;
        }

        mixpanel.track(TrackableEvents.ButtonClicked, {
          button_location: 'Mass Notify',
          button_feature: 'Mass Notify',
          button_label: 'Search Template',
          button_type: 'input',
        });

        setFilteredTemplates(
          templatesList.filter(ms => {
            return (
              ms.headline?.toLowerCase().includes(normalizedValue) ||
              ms.message?.toLowerCase().includes(normalizedValue) ||
              ms.senderText?.toLowerCase().includes(normalizedValue)
            );
          })
        );
      }
    },
    [historyList, templatesList]
  );

  useEffect(() => {
    if (isDeleteTemplateSuccess) {
      mixpanel.track(TrackableEvents.MassNotifyCreated, {
        user: userData?.profile?.sub,
        recipients: communication.recipients,
        message: communication.message,
        channel: communication.channel,
      });
      dispatch(openToast({ variant: 'success', header: 'Template deleted' }));
    } else if (isDeleteTemplateError) {
      setCommunication({ ...communication, isTemplate: true });
      notifyError(deleteTemplateError as ModelStateErrorResponse, 'Error deleting template');
    }
  }, [isDeleteTemplateSuccess, isDeleteTemplateError, deleteTemplateError]);

  const resetCommunication = () => {
    setCommunication({
      recipients: [],
      headline: '',
      senderText: `${currentUser?.fullName || ''} at ${currentUser?.customerName} using Perry Weather`,
      message: '',
      overridePreferences: DEFAULT_OVERRIDE_PREFERENCE,
      channel: DEFAULT_CHANNEL,
    });
  };

  const notifyError = (error: ModelStateErrorResponse, header: string) => {
    const massMessageError = error.data.responseException!.errors as CommunicationErrorModel;
    if (massMessageError) {
      setErrorState({ ...massMessageError });

      if (massMessageError.ServerError && massMessageError.ServerError.length > 0)
        dispatch(openToast({ variant: 'error', header: massMessageError.ServerError[0] }));
      else dispatch(openToast({ variant: 'error', header: header }));
    } else dispatch(openToast({ variant: 'error', header: header }));
  };

  if (!hasMassNotify) {
    return <Redirect to='/Unauthorized' />;
  }

  return (
    <TrackedPage
      docTitle='Notify Users'
      title={
        <Typography variant='h3' color='textPrimary' noWrap>
          Notify Users
        </Typography>
      }
      subheader={
        <Typography variant='body1' color='textSecondary'>
          Send a one-way text message and email to all or some of the users in your organization.
        </Typography>
      }>
      <Grid container spacing={4}>
        <Grid item md={8} sm={12}>
          <MessageForm
            currentUser={currentUser}
            users={users}
            sites={sites}
            notiUsers={notiUsers}
            userData={userData}
            communication={communication}
            storedCommunication={storedCommunication.current}
            errorState={errorState}
            onSaveTemplate={() => {
              setSelectedSubmenu('TEMPLATES');
              storedCommunication.current = communication;
            }}
            onSendMessage={() => setSelectedSubmenu('HISTORY')}
            onFormChange={value => setCommunication(value)}
            resetCommunication={resetCommunication}
            updateError={value => setErrorState(value)}
            notify={notifyError}
          />
        </Grid>

        <Grid container item md={4} sm={12} style={{ overflow: 'hidden' }}>
          <Card>
            <ToggleGroup
              options={SUBMENUS}
              value={selectedSubmenu}
              sx={{ container: { width: '92%', marginTop: 2, alignSelf: 'center' } }}
              onChange={handleSubmenuChange}
            />

            <TextField
              size='small'
              variant='outlined'
              value={searchString}
              placeholder={`Search Message ${SUBMENUS.find(x => x.value === selectedSubmenu)?.label}`}
              sx={{ marginTop: 3, marginX: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: theme.palette.text.primary }} />
                  </InputAdornment>
                ),
              }}
              onChange={e => {
                handleSearch(e.target.value, selectedSubmenu);
              }}
            />

            <Divider style={{ margin: '20px 0' }} />

            {selectedSubmenu === 'HISTORY' && (
              <>
                {filteredHistory && filteredHistory.length > 0 ? (
                  <>
                    <Stack flexGrow={1} justifyContent='flex-start' gap='20px' marginBottom={2}>
                      {slicedHistory &&
                        slicedHistory.map(mh => (
                          <MessageCard key={mh.id} message={mh} onCopyMessage={handleCopyMessage} />
                        ))}
                    </Stack>
                    <Box bottom='8px'>
                      <Pagination
                        count={Math.ceil(filteredHistory.length / AMT_PER_PAGE)}
                        page={currentPage}
                        onChange={(event, page) => {
                          setCurrentPage(page);
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box height='100%' display='flex' flexDirection='column' justifyContent='center'>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Chat style={{ width: 85, height: 85 }} htmlColor={theme.palette.primary.main} />
                    </div>
                    <Typography variant='body1' color='textSecondary'>
                      You haven't sent any messages.
                    </Typography>
                    <Typography variant='body1' color='textSecondary'>
                      Try sending one!
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {selectedSubmenu === 'TEMPLATES' && (
              <>
                {filteredTemplates && filteredTemplates.length > 0 ? (
                  <>
                    <Stack flexGrow={1} justifyContent='flex-start' gap='20px' marginBottom={2}>
                      {slicedTemplates &&
                        slicedTemplates.map(mh => (
                          <MessageCard
                            key={mh.id}
                            message={mh}
                            isLoading={isDeleteTemplateLoading}
                            onCopyMessage={handleCopyMessage}
                            onDelete={handleDelete}
                          />
                        ))}
                    </Stack>
                    <Box bottom='8px'>
                      <Pagination
                        count={Math.ceil(filteredTemplates.length / AMT_PER_PAGE)}
                        page={currentPage}
                        onChange={(event, page) => {
                          setCurrentPage(page);
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box height='100%' display='flex' flexDirection='column' justifyContent='center'>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Chat style={{ width: 85, height: 85 }} htmlColor={theme.palette.primary.main} />
                    </div>
                    <Typography variant='body1' color='textSecondary'>
                      You haven't saved any templates.
                    </Typography>
                    <Typography variant='body1' color='textSecondary'>
                      Try saving one!
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Grid>
        <Modal
          disabled={false}
          modalState={{ open: openDeleteConfirmationModal, handleOpen: () => {}, handleClose: () => {} }}
          sx={{ paper: { backgroundColor: theme.palette.background.default } }}>
          <Stack>
            <Typography variant={'subtitle1'}>Confirm you would like to delete the template?</Typography>
            <Stack direction='row' marginTop={4} gap={1} alignSelf='end'>
              <Button type='clear' onClick={() => handleCancelDelete()}>
                <Typography variant='body1'>Cancel</Typography>
              </Button>
              <Button onClick={() => handleDeleteTemplate()}>
                <Typography variant='body1'>Delete</Typography>
              </Button>
            </Stack>
          </Stack>
        </Modal>
      </Grid>
    </TrackedPage>
  );
}

export default MassNotify;
